import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Settings, Wrench, ShieldCheck, Wind, Headphones, Zap, ArrowRight } from 'lucide-react';
const services = [{
  id: 'installation',
  title: 'Installation',
  icon: Settings,
  description: 'Bespoke system design and installation for commercial buildings.',
  for: 'new builds, refits, expansions',
  href: '/services/installation'
}, {
  id: 'maintenance',
  title: 'Maintenance',
  icon: Wrench,
  description: 'Proactive servicing with detailed reporting and flexible contracts.',
  for: 'existing system owners, FM teams',
  href: '/services/maintenance'
}, {
  id: 'tm44',
  title: 'TM44 compliance',
  icon: ShieldCheck,
  description: 'Energy efficiency inspections, plain-English reports, remediation support.',
  for: 'building owners, landlords',
  href: '/services/tm44'
}, {
  id: 'ventilation',
  title: 'Ventilation',
  icon: Wind,
  description: 'Sector-specific air quality solutions and compliance sign-off.',
  for: 'offices, retail, hospitality',
  href: '/services/ventilation'
}, {
  id: 'fm-support',
  title: 'AC support for FM & property teams',
  icon: Headphones,
  description: 'White‑label and in‑house AC support for landlords, property managers and M&E contractors nationwide, integrating with existing FM providers.',
  for: 'facilities managers, M&E contractors',
  href: '/#contact'
}, {
  id: 'reactive-support',
  title: 'Reactive support',
  icon: Zap,
  description: 'Emergency commercial AC repairs. Same-day response where possible.',
  for: 'any commercial site',
  href: '/#contact'
}];
export function ServicesOverview() {
  return <main className="min-h-screen font-sans text-primary-dark pt-28 bg-[#E8F0F8]" data-id="element-2648">
      {/* Hero Section */}
      <section className="container mx-auto px-6 pb-20 pt-8" data-id="element-2649">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start" data-id="element-2650">
          <div className="lg:col-span-7" data-id="element-2651">
            <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/40 mb-6 block" data-id="element-2652">
              Our Services
            </span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-dark leading-[1.1] tracking-tight" data-id="element-2653">
              Commercial HVAC services
            </motion.h1>
          </div>
          <div className="lg:col-span-5 lg:pt-14" data-id="element-2654">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2655">
              Services built around your building, not a price list. From
              bespoke installation design to TM44 compliance, every service is
              delivered by our in-house commercial engineers.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="container mx-auto px-6 pb-24" data-id="element-2656">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2657">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} data-id="element-2658">
                <Link to={service.href} className="group flex flex-col h-full bg-white rounded-3xl p-8 border border-primary-dark/5 hover:border-accent/30 hover:shadow-lg transition-all duration-300" data-id="element-2659">
                  <div className="w-14 h-14 bg-[#E8F0F8] rounded-2xl flex items-center justify-center mb-8 text-accent group-hover:scale-110 transition-transform duration-300" data-id="element-2660">
                    <Icon className="w-7 h-7" data-id="element-2661" />
                  </div>
                  <h2 className="text-2xl font-serif text-primary-dark mb-4 group-hover:text-accent transition-colors" data-id="element-2662">
                    {service.title}
                  </h2>
                  <p className="text-primary-dark/70 mb-6 leading-relaxed flex-grow" data-id="element-2663">
                    {service.description}
                  </p>
                  <div className="mb-8 pt-6 border-t border-primary-dark/5" data-id="element-2664">
                    <div className="text-xs font-bold uppercase tracking-wider text-primary-dark/40 mb-1" data-id="element-2665">
                      For
                    </div>
                    <div className="text-sm text-primary-dark/80 font-medium" data-id="element-2666">
                      {service.for}
                    </div>
                  </div>
                  <div className="flex items-center text-sm font-medium text-accent mt-auto" data-id="element-2667">
                    Learn more
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2668" />
                  </div>
                </Link>
              </motion.div>;
        })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-dark py-24" data-id="element-2669">
        <div className="container mx-auto px-6 text-center" data-id="element-2670">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2671">
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-2672">
              Not sure which service you need?
            </h2>
            <p className="text-white/70 mb-10 text-lg leading-relaxed" data-id="element-2673">
              Talk to our engineers — we will recommend the right approach for
              your building.
            </p>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2674">
              Get a free consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2675" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}